import { EFinancingProgram } from '@if/core/types'
import { formatCurrency, formatDate } from '@if/core/utils'
import { Help } from '@if/mui'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material'
import { FormatPromoCode, FormatPromoCodeWithRates } from '@src/containers/App/utils/Formatter'
import { hasUserRight, useUser } from '@src/contexts/AppContext'
import useFeatureFlags from '@src/data/api/featureSwitch-api/featureSwitch-api'
import {
  PromoPlanDto,
  useConfirmCustomBuydownSwitch,
  useGetCurrentPromoPlans,
  useGetMerchantPaymentPlans,
} from '@src/data/api/merchants-api/merchants-api'
import { CUSTOM_BUYDOWN_FAMILY, EBannerOwnedFieldKey, EUserRights } from '@src/data/types/Constants'
import { Merchant } from '@src/data/types/Merchant'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

// Rates and fees are stored as fractions (0.06 → 6%), matching the edit-side BuydownCard.
const formatPercent = (value: number) => `${(value * 100).toFixed(2)}%`

// Mirrors IcebergUserExtensions.EnsureCanConfirmCustomBuydownSwitch on the API side.
const CUSTOM_BUYDOWN_SWITCH_CONFIRM_ROLES = ['ClientRelation', 'ClientRelationsAdmin', 'SystemAdmin']

type PlanStatus = PromoPlanDto['status']
type Inheritance = 'managed' | 'freestanding'

const SectionCard = ({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: ReactNode
}) => (
  <Card variant="outlined">
    <CardHeader
      title={title}
      subheader={description}
      slotProps={{ title: { variant: 'subtitle1', color: 'primary' }, subheader: { variant: 'body2' } }}
    />
    <CardContent sx={{ pt: 0 }}>{children}</CardContent>
  </Card>
)

// A caption-over-value fact, with an optional info tooltip explaining the field.
const Fact = ({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) => (
  <Box>
    <Stack direction="row" alignItems="center" gap={0.5}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {hint && <Help title={hint} />}
    </Stack>
    <Typography>{value}</Typography>
  </Box>
)

// Responsive CSS-grid track shared by the fact rows. auto-fit (not auto-fill) collapses empty
// trailing tracks so a partial set of facts stretches to fill a full row instead of leaving a gap.
const factGridSx = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2 } as const

const StatusPill = ({ status }: { status: PlanStatus }) => {
  const { t } = useTranslation()
  const color = status === 'active' ? 'success' : 'error'
  return (
    <Chip
      size="small"
      variant="outlined"
      color={color}
      label={t(`merchant.promotionsTab.status.${status}`)}
    />
  )
}

const BannerBadge = ({ inheritance }: { inheritance: Inheritance }) => {
  const { t } = useTranslation()
  if (inheritance === 'managed') {
    return (
      <Chip
        size="small"
        variant="outlined"
        color="primary"
        icon={<LockOutlinedIcon />}
        label={t('merchant.promotionsTab.managedByBanner')}
      />
    )
  }
  return null
}

const CustomBuydownCard = ({ plan, inheritance }: { plan: PromoPlanDto; inheritance: Inheritance }) => {
  const { t } = useTranslation()
  const status = plan.status
  const termRates = [...plan.promoTermRates].sort((a, b) => a.termMonths - b.termMonths)
  const inheritanceHint = t(
    inheritance === 'managed'
      ? 'merchant.promotionsTab.managedByBannerHint'
      : 'merchant.promotionsTab.freestandingHint',
  )

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack gap={2}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('merchant.promotionsTab.custom.promotionApplied')}
            </Typography>
            <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap" mt={0.5}>
              <Typography variant="h6" component="span" fontFamily="monospace">
                {plan.promoCodePrefix || plan.label}
              </Typography>
              <BannerBadge inheritance={inheritance} />
              <StatusPill status={status} />
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
              {inheritanceHint} {t(`merchant.promotionsTab.status.${status}Hint`)}
            </Typography>
          </Box>

          <Box sx={factGridSx}>
            <Fact
              label={t('merchant.promotionsTab.merchantContribution')}
              value={formatPercent(plan.merchantFeeRate)}
              hint={t('merchant.promotionsTab.merchantContributionHint')}
            />
            <Fact
              label={t('merchant.promotionsTab.adminFee')}
              value={formatPercent(plan.borrowerFeeRate)}
              hint={t(
                hasUserRight(EUserRights.canEditAdminFee)
                  ? 'merchant.promotionsTab.adminFeeHintEditable'
                  : 'merchant.promotionsTab.adminFeeHint',
              )}
            />
            <Fact
              label={t('merchant.promotionsTab.maxFinancingAmount')}
              value={
                plan.promoTermRates[0]?.maxFinancingAmount
                  ? formatCurrency(plan.promoTermRates[0].maxFinancingAmount)
                  : t('merchant.promotionsTab.noMaximum')
              }
              hint={t('merchant.promotionsTab.maxFinancingAmountHint')}
            />
            <Fact label={t('merchant.promo.activeFrom')} value={formatDate(plan.activeFrom)} />
          </Box>

          <Box>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Typography variant="caption" color="text.secondary">
                {t('merchant.promotionsTab.customerRates')}
              </Typography>
              <Help title={t('merchant.promotionsTab.customerRatesHint')} />
            </Stack>
            <Box sx={{ mt: 0.5, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 1 }}>
              {termRates.map((rate) => (
                <Box
                  key={rate.termMonths}
                  sx={{ textAlign: 'center', bgcolor: 'action.hover', borderRadius: 1, py: 0.75, px: 1 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {rate.termMonths} {t('common.months')}
                  </Typography>
                  <Typography variant="h4" fontSize="1.4rem" fontWeight={700}>
                    {formatPercent(rate.promoRate)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}

interface Props {
  merchant: Merchant
}

const MerchantPromotionsTab = ({ merchant }: Props) => {
  const { t } = useTranslation()
  const [featureFlags, isFetchingFlags] = useFeatureFlags()
  const [merchantPaymentPlans] = useGetMerchantPaymentPlans(merchant.id)
  const [currentPlans, isFetchingCurrentPlans] = useGetCurrentPromoPlans(merchant.id)
  const [confirmSwitch, isConfirmingSwitch] = useConfirmCustomBuydownSwitch()
  const user = useUser()

  // With the Custom Buydown kill switch off, a stored request stays invisible and unconfirmable:
  // confirming would switch a merchant into a mode nothing else honours.
  const pendingSwitchRequestOn = featureFlags?.useCustomFlashPlan ? merchant.customBuydownSwitchRequestedOn : null
  const canConfirmSwitch = user?.roles.some((role) => CUSTOM_BUYDOWN_SWITCH_CONFIRM_ROLES.includes(role)) ?? false

  // Detection mirrors EditMerchant 1:1 — no new field.
  const optedIn = merchant.flashPlanDefinedInManageMerchant
  const isCustomBuydown = !!featureFlags?.useCustomFlashPlan && optedIn
  const isIncoherent = !!featureFlags && !featureFlags.useCustomFlashPlan && optedIn

  // Managed = the parent banner owns (and locks) the buydown on this child, read off the child's own
  // bannerOwnedFields — no banner fetch or divergence comparison needed.
  const isManaged = (merchant.bannerOwnedFields ?? []).includes(EBannerOwnedFieldKey.CustomBuydown)
  const inheritance: Inheritance = isManaged ? 'managed' : 'freestanding'

  // Wait for the feature flags before choosing a branch, so a Custom-Buydown merchant never flashes
  // the preconfigured rendering while flags load.
  if (isFetchingFlags) {
    return (
      <Stack direction="row" justifyContent="center" alignItems="center" padding={4}>
        <CircularProgress />
      </Stack>
    )
  }

  const renderBody = () => {
    if (isIncoherent) {
      return (
        <Alert severity="warning">
          <AlertTitle>{t('merchant.promotionsTab.incoherentTitle')}</AlertTitle>
          {t('merchant.promotionsTab.incoherent')}
        </Alert>
      )
    }

    if (isCustomBuydown) {
      if (isFetchingCurrentPlans) {
        return (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )
      }
      const activePlan = currentPlans.find((plan) => plan.status === 'active')
      return (
        <Stack gap={2}>
          {isManaged && <Alert severity="info">{t('merchant.promotionsTab.managedByBannerHint')}</Alert>}
          {!activePlan ? (
            <SectionCard
              title={t('merchant.promotionsTab.custom.title')}
              description={t('merchant.promotionsTab.custom.description')}
            >
              <Typography color="text.secondary" fontStyle="italic">
                {t('merchant.promotionsTab.noCustomPlan')}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                {t('merchant.promotionsTab.noCustomPlanHint')}
              </Typography>
            </SectionCard>
          ) : (
            <>
              <Box>
                <Typography variant="subtitle1" color="primary">
                  {t('merchant.promotionsTab.custom.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('merchant.promotionsTab.custom.description')}
                </Typography>
              </Box>
              <CustomBuydownCard plan={activePlan} inheritance={inheritance} />
            </>
          )}
        </Stack>
      )
    }

    // Preconfigured (today's behaviour, unchanged).
    const preconfiguredPlans = (merchantPaymentPlans ?? [])
      .filter((plan) => !plan.expiresOn || new Date() <= new Date(plan.expiresOn))
      // The dual-write system mirrors the Custom Buydown into a legacy payment plan for
      // compatibility; it is not a preconfigured promotion, so hide it from this list.
      .filter((plan) => FormatPromoCode(plan.promoCode) !== CUSTOM_BUYDOWN_FAMILY)
    return (
      <>
        {pendingSwitchRequestOn && (
          <Alert
            severity="info"
            action={
              canConfirmSwitch ? (
                <Button size="small" disabled={isConfirmingSwitch} onClick={() => confirmSwitch(merchant.id)}>
                  {t('merchant.promotionsTab.switchRequest.confirm')}
                </Button>
              ) : undefined
            }
          >
            <AlertTitle>{t('merchant.promotionsTab.switchRequest.title')}</AlertTitle>
            {t('merchant.promotionsTab.switchRequest.body', {
              date: formatDate(pendingSwitchRequestOn),
              emails: merchant.promoNotificationEmailList.join(', '),
            })}
          </Alert>
        )}
        <SectionCard
          title={t('merchant.promotionsTab.preconfigured.title')}
          description={t('merchant.promotionsTab.preconfigured.description')}
        >
          {preconfiguredPlans.length > 0 && (
            <>
              <Typography variant="caption" color="text.secondary">
                {t('merchant.promotionsTab.preconfigured.promotionApplied')}
              </Typography>
              <Stack mt={0.5}>
                {preconfiguredPlans.map((plan) => (
                  <Typography key={plan.id} fontFamily="monospace" variant="body2">
                    {FormatPromoCodeWithRates(plan)}
                  </Typography>
                ))}
              </Stack>
            </>
          )}
        </SectionCard>
        {merchant?.planPromoRules.length > 0 && (
          <SectionCard
            title={t('merchant.promotionsTab.defaultApplied.title')}
            description={t('merchant.promotionsTab.defaultApplied.description')}
          >
            <Box sx={factGridSx}>
              {merchant.planPromoRules?.map((entry) => (
                <Fact
                  key={entry.defaultPromoCode}
                  label={`${t(`EFinancingProgram.${entry.financingProgramId as EFinancingProgram}`, { ns: 'enum' })} ${t(`EPlanType.${entry.planName}`, { ns: 'enum' })}`}
                  value={FormatPromoCode(entry.defaultPromoCode)}
                />
              ))}
            </Box>
          </SectionCard>
        )}
      </>
    )
  }

  return (
    <Stack spacing={2} padding={2}>
      <Typography variant="body2" color="text.secondary">
        {t('merchant.promotionsTab.intro')}
      </Typography>
      {renderBody()}
    </Stack>
  )
}

export default MerchantPromotionsTab
